import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Grid from '../components/Grid';
import Pagination from '../components/Pagination';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { Snackbar, Alert } from '@mui/material';
import { API_BASE_URL, AUTHORIZED_ROLES } from '../constants/property';

const UserDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasAnyRole } = useAuth();
  const isAdministrator = hasAnyRole(AUTHORIZED_ROLES);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [userDetailsData, setUserDetailsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [sortColumn, setSortColumn] = useState('dateOfJoining');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [phoneNumberError, setPhoneNumberError] = useState('');
  const [alternatePhoneNumberError, setAlternatePhoneNumberError] = useState('');
  const [roleOptionsFromApi, setRoleOptionsFromApi] = useState([]);
  const defaultVisibleColumns = {
    employeeId: true,
    name: true,
    userName: true,
    role: true,
    email: true,
    phone: true,
    alternatePhone: true,
    dateOfJoining: true,
    password: true,
    actions: true
  };
  const [visibleColumns, setVisibleColumns] = useState(defaultVisibleColumns);
  
  // Filter states
  const [showFilters, setShowFilters] = useState(true);
  const [datePreset, setDatePreset] = useState('');
  const [dateDisplay, setDateDisplay] = useState('');
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [showCustomCalendar, setShowCustomCalendar] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedName, setSelectedName] = useState('all');
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('all');
  const [nameInputValue, setNameInputValue] = useState('');
  const [showNameDropdown, setShowNameDropdown] = useState(false);
  const [roleInputValue, setRoleInputValue] = useState('');
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [employeeIdInputValue, setEmployeeIdInputValue] = useState('');
  const [showEmployeeIdDropdown, setShowEmployeeIdDropdown] = useState(false);
  
  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const [formData, setFormData] = useState({
    empId: '',
    employeeId: '',
    empName: '',
    firstName: '',
    lastName: '',
    userName: '',
    employeeName: '',
    roleId: '',
    role: '',
    email: '',
    phoneNumber: '',
    alternatePhoneNumber: '',
    dateOfJoining: getTodayDate(),
    password: '',
   
  });
  const [showPassword, setShowPassword] = useState(false);

  // Check if modal should be opened from navigation
  useEffect(() => {
    if (location.state?.openModal) {
      setIsModalOpen(true);
      // Clear the state to prevent reopening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Fetch roles for Role dropdown
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/role`, {
          method: 'GET',
          mode: 'cors'
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        const options = (Array.isArray(data) ? data : [])
          .map((item) => {
            const label = item.roleName || item.name || item.roleCode;
            return label ? { value: label, label } : null;
          })
          .filter(Boolean);

        setRoleOptionsFromApi(options);
      } catch (err) {
        console.error('Error fetching roles:', err);
        // Fallback to static role options in the dropdown if API fails
      }
    };

    fetchRoles();
  }, []);

  // API Functions without token validation
  const fetchUserDetailsData = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/employee`, {
        method: 'GET',
        mode: 'cors'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
        console.log('Employee API Response:', data);
      // Process data to ensure all records have employee IDs
      const processedData = processEmployeeData(data);
      setUserDetailsData(processedData);
      
    } catch (err) {
      console.error('Error fetching employee data:', err);
    //   setError(err.message || 'Failed to fetch user details data');
      // Fallback to static data if API fails
      const staticData = getStaticUserDetailsData();
      const processedStaticData = processEmployeeData(staticData);
      setUserDetailsData(processedStaticData);
    } finally {
      setIsLoading(false);
    }
  };


  const createUserDetailsRecord = async (recordData) => {
    try {
      // Format data to match API requirements
      const apiData = {
        employeeName:  String(recordData.userName || '').trim().slice(0, 10),
        userName: String(recordData.userName || '').trim().slice(0, 10),
        firstName: recordData.firstName || '',
        lastName: recordData.lastName || '',
        email: recordData.email || '',
        phoneNumber: recordData.phoneNumber || '',
        alternatePhoneNumber: recordData.alternatePhoneNumber || '',
        role: recordData.role || '',
        dateOfJoining: recordData.dateOfJoining || '',
        password: recordData.password || ''

      };
      
      console.log('Create API Call - Data (formatted):', apiData);
      
      const response = await fetch(`${API_BASE_URL}/api/employee`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(apiData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create employee record');
      }

      const data = await response.json();
      console.log('Create employee response:', data);
      return data;
      
    } catch (err) {
      console.error('Error creating employee record:', err);
      throw err;
    }
  };

  const updateUserDetailsRecord = async (recordData) => {
    try {
      const url = `${API_BASE_URL}/api/employee`;
      
      // Format data to match API requirements
      const apiData = {
        empID: recordData.empId || recordData.empID, // Use empID (capital ID) for API
        employeeName:   String(recordData.userName || '').trim().slice(0, 10),
        userName: String(recordData.userName || '').trim().slice(0, 10),
        firstName: recordData.firstName || '',
        lastName: recordData.lastName || '',
        email: recordData.email || '',
        phoneNumber: recordData.phoneNumber || '',
        alternatePhoneNumber: recordData.alternatePhoneNumber || '',
        employeeId: recordData.employeeId,
        role: recordData.role || '',
        dateOfJoining: recordData.dateOfJoining || '',
        password: recordData.password || ''
      };
      
      console.log('Update API Call:', url);
      console.log('Update Data (formatted):', apiData);
      console.log('Update ID (empID):', apiData.empID);
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(apiData)
      });

      console.log('Update Response Status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Update Error Response:', errorData);
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Update employee response:', data);
      return data;
      
    } catch (err) {
      console.error('Error updating employee record:', err);
      throw err;
    }
  };

  const deleteUserDetailsRecord = async (id) => {
    try {
      const url = `${API_BASE_URL}/api/employee/${id}`;
      console.log('Delete API Call:', url);
      console.log('Delete ID:', id);
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      console.log('Delete Response Status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Delete Error Response:', errorData);
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      console.log('Delete employee response:', response);
      return true;
      
    } catch (err) {
      console.error('Error deleting employee record:', err);
      throw err;
    }
  };


  // Generate dynamic role based on employee ID
  const generateEmployeeRole = (empId) => {
    if (!empId) return 'Staff';
    
    // Extract number from employee ID
    const empNumber = parseInt(empId.replace('EMP', ''));
    
    // Define role distribution pattern
    const roles = ['Staff', 'Supervisor', 'Manager', 'Helper 1', 'Helper 2'];
    
    // Use modulo to distribute roles evenly
    const roleIndex = (empNumber - 1) % roles.length;
    return roles[roleIndex];
  };

  // Load attendance data on component mount
  useEffect(() => {
    console.log('Attendance component mounted, calling fetchAttendanceData');
    fetchUserDetailsData();
  }, []);

  // Static data fallback
  const getStaticUserDetailsData = () => {
    return [
    {
      id: 1,
      date: "Oct 09, 2025",
      empId: "TKD001",
      firstName: "Selva",
      lastName: "Kumar",
      role: "Supervisor",
      attendanceDate: "2025-01-15",
      email: "selva@gmail.com",
      phoneNumber: "1234567890",
      alternatePhoneNumber: "1234567890",
    },
    {
      id: 2,
      date: "Oct 08, 2025",
      empId: "TKD002",
      firstName: "Emma",
      lastName: "Wilson",
      role: "Staff",
      attendanceDate: "2025-01-15",
      email: "emma@gmail.com",  
      phoneNumber: "1234567890",
      alternatePhoneNumber: "1234567890",
    },
    {
      id: 3,
      date: "Oct 07, 2025",
      empId: "TKD003",
      firstName: "Mark",
      lastName: "Johnson",
      role: "Manager",
      attendanceDate: "2025-01-15",
      email: "mark@gmail.com",
      phoneNumber: "1234567890",
      alternatePhoneNumber: "1234567890",
    },
    {
      id: 4,
      date: "Oct 06, 2025",
      empId: "TKD004",
      firstName: "John",
      lastName: "Doe",
      role: "Staff",
      attendanceDate: "2025-01-15",
      email: "john@gmail.com",
      phoneNumber: "1234567890",
      alternatePhoneNumber: "1234567890",
    },
    {
      id: 5,
      date: "Oct 06, 2025",
      empId: "TKD005",
      firstName: "Lily",
      lastName: "Brown",
      role: "Supervisor",
      attendanceDate: "2025-01-15",
      email: "lily@gmail.com",
      phoneNumber: "1234567890",
      alternatePhoneNumber: "1234567890",
    },
    {
      id: 6,
      date: "Oct 01, 2025",
      empId: "TKD006",
      firstName: "Daniel",
      lastName: "Smith",
      role: "Staff",
      attendanceDate: "2025-01-15",
      email: "daniel@gmail.com",
      phoneNumber: "1234567890",
      alternatePhoneNumber: "1234567890",
    },
    {
      id: 7,
      date: "Oct 01, 2025",
      empId: "TKD007",
      firstName: "Peter",
      lastName: "Anderson",
      role: "Manager",
      attendanceDate: "2025-01-15",
      email: "peter@gmail.com",
      phoneNumber: "1234567890",
      alternatePhoneNumber: "1234567890",
    },
    {
      id: 8,
      date: "Oct 01, 2025",
      empId: "TKD008",
      firstName: "Jude",
      lastName: "Davis",
      role: "Staff",
      attendanceDate: "2025-01-15",
      email: "jude@gmail.com",
      phoneNumber: "1234567890",
      alternatePhoneNumber: "1234567890",
    },
    {
      id: 9,
      date: "Oct 01, 2025",
      empId: "TKD009",
      firstName: "Robison",
      lastName: "Taylor",
      role: "Manager",
      attendanceDate: "2025-01-15",
      email: "robison@gmail.com",
      phoneNumber: "1234567890",
      alternatePhoneNumber: "1234567890",
    },
    {
      id: 10,
      date: "Oct 01, 2025",
      empId: "TKD010",
      firstName: "Sarah",
      lastName: "Wilson",
      role: "Supervisor",
      attendanceDate: "2025-01-15",
      email: "sarah@gmail.com",
      phoneNumber: "1234567890",
      alternatePhoneNumber: "1234567890",
    }
  ];
  };

  // Process data to ensure all records have employee IDs and roles
  const processEmployeeData = (data) => {
    return data.map((record, index) => {
      let updatedRecord = { ...record };
      
      // // Ensure id field exists (API might use different field names)
      // if (!updatedRecord.id && updatedRecord.employeeId) {
      //   updatedRecord.id = updatedRecord.employeeId;
      // }
      // if (!updatedRecord.id && updatedRecord.empId) {
      //   // Use empId as fallback if no id exists
      //   updatedRecord.id = updatedRecord.empId;
      // }
      
      // // Generate employee ID if missing
      // if (!record.empId) {
      //   const generatedId = `EMP${(index + 1).toString().padStart(3, '0')}`;
      //   updatedRecord.empId = generatedId;
      // }
      
      // // Generate role if missing
      // if (!record.role) {
      //   updatedRecord.role = generateEmployeeRole(updatedRecord.empId);
      // }
      
      return updatedRecord;
    });
  };

  // Filter options
  const nameOptions = useMemo(() => {
    const uniqueNames = [...new Set(userDetailsData.map(item => `${item.firstName} ${item.lastName}`.trim()))];
    return [{ value: 'all', label: 'All' }, ...uniqueNames.map(name => ({ value: name, label: name }))];
  }, [userDetailsData]);

  const roleOptions = useMemo(() => {
    const uniqueRoles = [...new Set(userDetailsData.map(item => item.role).filter(Boolean))];
    return [{ value: 'all', label: 'All' }, ...uniqueRoles.map(role => ({ value: role, label: role }))];
  }, [userDetailsData]);

  const employeeIdOptions = useMemo(() => {
    const uniqueEmployeeIds = [...new Set(userDetailsData.map(item => item.empId).filter(Boolean))];
    return [{ value: 'all', label: 'All' }, ...uniqueEmployeeIds.map(empId => ({ value: empId, label: empId }))];
  }, [userDetailsData]);

  // Date preset display and handling
  useEffect(() => {
    if (!datePreset) {
      setDateDisplay('');
      return;
    }
    
    const now = new Date();
    // Use same "Mar 16, 2026" style as table
    const toDisplay = (d) => d.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
    const clone = (d) => new Date(d.getTime());
    let start = clone(now);
    let end = clone(now);
    
    switch (datePreset) {
      case 'today':
        break;
      case 'yesterday':
        start.setDate(start.getDate() - 1); 
        end.setDate(end.getDate() - 1); 
        break;
      case 'last7':
        start.setDate(start.getDate() - 6); 
        break;
      case 'last30':
        start.setDate(start.getDate() - 29); 
        break;
      case 'thisMonth':
        start = new Date(now.getFullYear(), now.getMonth(), 1); 
        break;
      case 'lastMonth': {
        const firstLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
        start = firstLastMonth; 
        end = lastLastMonth; 
        break;
      }
      case 'custom':
        if (!dateDisplay) {
          setDateDisplay(`${toDisplay(start)} - ${toDisplay(end)}`);
        }
        return;
      default:
        break;
    }
    setDateDisplay(`${toDisplay(start)} - ${toDisplay(end)}`);
  }, [datePreset]);

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDate = (date) => {
    if (!date) return '';
    // Match "Mar 16, 2026" style
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
  };

  const isSameDate = (date1, date2) => {
    if (!date1 || !date2) return false;
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  };

  const isDateInRange = (date, startDate, endDate) => {
    if (!startDate || !endDate || !date) return false;
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const s = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const e = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    return d >= s && d <= e;
  };

  const handleDatePresetChange = (preset) => {
    setDatePreset(preset);
    setShowDateDropdown(false);
    if (preset === 'custom') {
      setShowCustomCalendar(true);
    } else {
      setShowCustomCalendar(false);
    }
  };

  const handleDateClick = (date) => {
    if (!customStartDate || (customStartDate && customEndDate)) {
      setCustomStartDate(date);
      setCustomEndDate(null);
    } else if (customStartDate && !customEndDate) {
      if (date < customStartDate) {
        setCustomEndDate(customStartDate);
        setCustomStartDate(date);
      } else {
        setCustomEndDate(date);
      }
    }
  };

  const handleApplyCustomRange = () => {
    if (customStartDate && customEndDate) {
      setDateDisplay(`${formatDate(customStartDate)} - ${formatDate(customEndDate)}`);
      setShowCustomCalendar(false);
      setShowDateDropdown(false);
    }
  };

  const handleCancelCustomRange = () => {
    setShowCustomCalendar(false);
    setShowDateDropdown(false);
    setCustomStartDate(null);
    setCustomEndDate(null);
  };

  const handleClearDate = (e) => {
    e.stopPropagation();
    setDatePreset('');
    setDateDisplay('');
    setCustomStartDate(null);
    setCustomEndDate(null);
    setShowCustomCalendar(false);
    setShowDateDropdown(false);
  };

  const handleClearEmployeeId = (e) => {
    e.stopPropagation();
    setSelectedEmployeeId('all');
    setEmployeeIdInputValue('');
    setShowEmployeeIdDropdown(false);
    setCurrentPage(1);
  };

  const handleClearName = (e) => {
    e.stopPropagation();
    setSelectedName('all');
    setNameInputValue('');
    setShowNameDropdown(false);
    setCurrentPage(1);
  };

  const handleClearRole = (e) => {
    e.stopPropagation();
    setSelectedRole('all');
    setRoleInputValue('');
    setShowRoleDropdown(false);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setDatePreset('');
    setDateDisplay('');
    setCustomStartDate(null);
    setCustomEndDate(null);
    setSelectedName('all');
    setSelectedRole('all');
    setSelectedEmployeeId('all');
    setNameInputValue('');
    setRoleInputValue('');
    setEmployeeIdInputValue('');
    setCurrentPage(1);
    setShowCustomCalendar(false);
    setShowDateDropdown(false);
    setShowNameDropdown(false);
    setShowRoleDropdown(false);
    setShowEmployeeIdDropdown(false);
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  // Name autocomplete logic
  const isTypingRef = useRef(false);
  
  useEffect(() => {
    if (!isTypingRef.current) {
      if (selectedName === 'all') {
        setNameInputValue('');
      } else {
        const selectedOption = nameOptions.find(opt => opt.value === selectedName);
        if (selectedOption) {
          setNameInputValue(selectedOption.label);
        } else {
          setNameInputValue('');
        }
      }
    }
  }, [selectedName, nameOptions]);

  const filteredNameOptions = useMemo(() => {
    if (!nameInputValue.trim()) {
      return nameOptions;
    }
    const searchTerm = nameInputValue.toLowerCase();
    return nameOptions.filter(opt => 
      opt.label.toLowerCase().includes(searchTerm)
    );
  }, [nameOptions, nameInputValue]);

  const handleNameInputChange = (e) => {
    const value = e.target.value;
    isTypingRef.current = true;
    setNameInputValue(value);
    setShowNameDropdown(true);
    
    if (!value.trim()) {
      setSelectedName('all');
    }
    
    setTimeout(() => {
      isTypingRef.current = false;
    }, 100);
  };

  const handleNameSelect = (option) => {
    isTypingRef.current = false;
    setSelectedName(option.value);
    setNameInputValue(option.label);
    setShowNameDropdown(false);
    setCurrentPage(1);
  };

  // Role autocomplete logic
  const isTypingRoleRef = useRef(false);
  
  useEffect(() => {
    if (!isTypingRoleRef.current) {
      if (selectedRole === 'all') {
        setRoleInputValue('');
      } else {
        const selectedOption = roleOptions.find(opt => opt.value === selectedRole);
        if (selectedOption) {
          setRoleInputValue(selectedOption.label);
        } else {
          setRoleInputValue('');
        }
      }
    }
  }, [selectedRole, roleOptions]);

  const filteredRoleOptions = useMemo(() => {
    if (!roleInputValue.trim()) {
      return roleOptions;
    }
    const searchTerm = roleInputValue.toLowerCase();
    return roleOptions.filter(opt => 
      opt.label.toLowerCase().includes(searchTerm)
    );
  }, [roleOptions, roleInputValue]);

  const handleRoleInputChange = (e) => {
    const value = e.target.value;
    isTypingRoleRef.current = true;
    setRoleInputValue(value);
    setShowRoleDropdown(true);
    
    if (!value.trim()) {
      setSelectedRole('all');
    }
    
    setTimeout(() => {
      isTypingRoleRef.current = false;
    }, 100);
  };

  const handleRoleSelect = (option) => {
    isTypingRoleRef.current = false;
    setSelectedRole(option.value);
    setRoleInputValue(option.label);
    setShowRoleDropdown(false);
    setCurrentPage(1);
  };

  // Employee ID autocomplete logic
  const isTypingEmployeeIdRef = useRef(false);
  
  useEffect(() => {
    if (!isTypingEmployeeIdRef.current) {
      if (selectedEmployeeId === 'all') {
        setEmployeeIdInputValue('');
      } else {
        const selectedOption = employeeIdOptions.find(opt => opt.value === selectedEmployeeId);
        if (selectedOption) {
          setEmployeeIdInputValue(String(selectedOption.label ?? ''));
        } else {
          setEmployeeIdInputValue('');
        }
      }
    }
  }, [selectedEmployeeId, employeeIdOptions]);

  const filteredEmployeeIdOptions = useMemo(() => {
    const employeeIdInputText = String(employeeIdInputValue ?? '');
    if (!employeeIdInputText.trim()) {
      return employeeIdOptions;
    }
    const searchTerm = employeeIdInputText.toLowerCase();
    return employeeIdOptions.filter(opt => 
      String(opt.label ?? '').toLowerCase().includes(searchTerm)
    );
  }, [employeeIdOptions, employeeIdInputValue]);

  const handleEmployeeIdInputChange = (e) => {
    const value = String(e.target.value ?? '');
    isTypingEmployeeIdRef.current = true;
    setEmployeeIdInputValue(value);
    setSelectedEmployeeId('all');
    setShowEmployeeIdDropdown(true);

    setTimeout(() => {
      isTypingEmployeeIdRef.current = false;
    }, 100);
  };

  const handleEmployeeIdSelect = (option) => {
    isTypingEmployeeIdRef.current = false;
    setSelectedEmployeeId(option.value);
    setEmployeeIdInputValue(String(option.label ?? ''));
    setShowEmployeeIdDropdown(false);
    setCurrentPage(1);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDateDropdown && !event.target.closest('.date-dropdown-container')) {
        setShowDateDropdown(false);
      }
      if (showNameDropdown && !event.target.closest('.name-autocomplete-container')) {
        setShowNameDropdown(false);
      }
      if (showRoleDropdown && !event.target.closest('.role-autocomplete-container')) {
        setShowRoleDropdown(false);
      }
      if (showEmployeeIdDropdown && !event.target.closest('.employee-id-autocomplete-container')) {
        setShowEmployeeIdDropdown(false);
      }
      if (showColumnMenu && !event.target.closest('.column-menu-container')) {
        setShowColumnMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDateDropdown, showNameDropdown, showRoleDropdown, showEmployeeIdDropdown, showColumnMenu]);

  const handleEmployeeClick = (employee) => {
    navigate('/employee-details', { state: { employee } });
  };

  // Check if any filters are applied
  const hasActiveFilters = useMemo(() => {
    const employeeIdSearchTerm = String(employeeIdInputValue ?? '').trim();
    return datePreset !== '' || dateDisplay !== '' || selectedName !== 'all' || selectedRole !== 'all' || employeeIdSearchTerm !== '';
  }, [datePreset, dateDisplay, selectedName, selectedRole, employeeIdInputValue]);

  const parseDateValue = (dateValue) => {
    if (!dateValue) return new Date(0);

    // Handle ISO-like "YYYY-MM-DD" explicitly to avoid timezone shifts
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      const [year, month, day] = dateValue.split('-').map(Number);
      return new Date(year, month - 1, day);
    }

    const parsed = new Date(dateValue);
    return Number.isNaN(parsed.getTime())
      ? new Date(0)
      : new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  };

  const formatDateMMMDDYYYY = (dateValue) => {
    if (!dateValue) return '-';
    const parsed = parseDateValue(dateValue);
    if (parsed.getTime() === 0) return '-';
    return parsed.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
  };

  // Compute filtered and sorted data
  const filteredData = useMemo(() => {
    // Determine active date range based on preset or custom selection
    let startDate = null;
    let endDate = null;

    const today = new Date();

    if (datePreset && datePreset !== 'custom') {
      switch (datePreset) {
        case 'today': {
          const d = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          startDate = d;
          endDate = d;
          break;
        }
        case 'yesterday': {
          const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
          startDate = d;
          endDate = d;
          break;
        }
        case 'last7': {
          endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6);
          break;
        }
        case 'last30': {
          endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29);
          break;
        }
        case 'thisMonth': {
          startDate = new Date(today.getFullYear(), today.getMonth(), 1);
          endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
          break;
        }
        case 'lastMonth': {
          startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          endDate = new Date(today.getFullYear(), today.getMonth(), 0);
          break;
        }
        default:
          break;
      }
    } else if (datePreset === 'custom' && customStartDate && customEndDate) {
      startDate = customStartDate;
      endDate = customEndDate;
    }

    let filtered = userDetailsData.filter(item => {
      const fullName = `${item.firstName} ${item.lastName}`.trim();
      const nameSearchTerm = String(nameInputValue ?? '').trim().toLowerCase();
      const nameOk = !nameSearchTerm || fullName.toLowerCase().includes(nameSearchTerm);
      const roleOk = selectedRole === 'all' || (item.role || '').toString().toLowerCase() === selectedRole.toString().toLowerCase();
      const employeeIdSearchTerm = String(employeeIdInputValue ?? '').trim().toLowerCase();
      // Filter strictly on employeeId field (as requested)
      const employeeIdValue = (item.employeeId || '').toString().toLowerCase();
      const employeeIdOk = !employeeIdSearchTerm || employeeIdValue.includes(employeeIdSearchTerm);

      let dateOk = true;
      if (startDate && endDate) {
        const doj = parseDateValue(item.dateOfJoining);
        dateOk = isDateInRange(doj, startDate, endDate);
      }

      return nameOk && roleOk && employeeIdOk && dateOk;
    });

    // Apply sorting
    if (sortColumn) {
      filtered = [...filtered].sort((a, b) => {
        let aValue, bValue;
        
        if (sortColumn === 'name') {
          aValue = `${a.firstName} ${a.lastName}`.trim().toLowerCase();
          bValue = `${b.firstName} ${b.lastName}`.trim().toLowerCase();
        } else if (sortColumn === 'employeeId') {
          aValue = (a.empId || a.employeeId || '').toString().toLowerCase();
          bValue = (b.empId || b.employeeId || '').toString().toLowerCase();
        } else if (sortColumn === 'dateOfJoining') {
          aValue = parseDateValue(a.dateOfJoining);
          bValue = parseDateValue(b.dateOfJoining);
        } else {
          aValue = (a[sortColumn] || '').toString().toLowerCase();
          bValue = (b[sortColumn] || '').toString().toLowerCase();
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [userDetailsData, nameInputValue, selectedRole, employeeIdInputValue, sortColumn, sortDirection, datePreset, customStartDate, customEndDate]);

  // Pagination logic (based on filtered results)
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, itemsPerPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return (
        <svg className="w-4 h-4 text-white opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    if (sortDirection === 'asc') {
      return (
        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      );
    }
    return (
      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => {
      const visibleCount = Object.values(prev).filter(v => v).length;
      if (prev[columnKey] && visibleCount === 1) {
        return prev;
      }
      return {
        ...prev,
        [columnKey]: !prev[columnKey]
      };
    });
  };
  const handleResetColumns = () => {
    setVisibleColumns(defaultVisibleColumns);
  };

  const columnDefinitions = [
    { key: 'employeeId', label: 'Emp ID' },
    { key: 'name', label: 'Name' },
    { key: 'userName', label: 'Username' },
    { key: 'role', label: 'Role' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'alternatePhone', label: 'Alternate Phone' },
    { key: 'dateOfJoining', label: 'Date of Joining' },
    { key: 'password', label: 'Password' },
    { key: 'actions', label: 'Actions' }
  ];

  const getVisibleColumnCount = () => {
    return Object.values(visibleColumns).filter(v => v).length;
  };

  const handleCloseSnackbar = (event, reason) => {
    setSnackbarOpen(false);
    setSuccessMessage('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'userName') {
      const limited = String(value).slice(0, 10);
      setFormData((prev) => ({ ...prev, userName: limited }));
      return;
    }

    if (name === 'phoneNumber' || name === 'alternatePhoneNumber') {
      const digitsOnly = value.replace(/\D/g, '');
      const limited = digitsOnly.slice(0, 10);
      if (name === 'phoneNumber') {
        const msg = limited.length === 0 ? '' : (limited.length !== 10 ? 'Phone number must be exactly 10 digits (not less, not more).' : '');
        setPhoneNumberError(digitsOnly.length > 10 ? 'Phone number must be exactly 10 digits (not more than 10).' : msg);
        setFormData(prev => ({ ...prev, phoneNumber: limited }));
      } else {
        const altMsg = limited.length === 0 ? '' : (limited.length !== 10 ? 'Alternate phone must be exactly 10 digits (not less, not more).' : '');
        setAlternatePhoneNumberError(digitsOnly.length > 10 ? 'Alternate phone must be exactly 10 digits (not more than 10).' : altMsg);
        setFormData(prev => ({ ...prev, alternatePhoneNumber: limited }));
      }
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (phoneNumberError || alternatePhoneNumberError) {
      setSuccessMessage('Phone number must be exactly 10 digits (not less, not more). Please correct and try again.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }
    const phoneDigits = (formData.phoneNumber || '').replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      setPhoneNumberError(phoneDigits.length === 0 ? 'Phone number is required and must be exactly 10 digits.' : 'Phone number must be exactly 10 digits (not less, not more).');
      setSuccessMessage('Phone number must be exactly 10 digits (not less, not more).');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }
    const altPhoneDigits = (formData.alternatePhoneNumber || '').replace(/\D/g, '');
    if (altPhoneDigits.length > 0 && altPhoneDigits.length !== 10) {
      setAlternatePhoneNumberError('Alternate phone must be exactly 10 digits or left empty.');
      setSuccessMessage('Alternate phone must be exactly 10 digits (not less, not more) or left empty.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }
    const userNameTrimmed = String(formData.userName || '').trim();
    if (!userNameTrimmed) {
      setSuccessMessage('Username is required (maximum 10 characters).');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }
    if (userNameTrimmed.length > 10) {
      setSuccessMessage('Username must be at most 10 characters.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }
    console.log('Form submitted:', formData);
    
    try {
      if (isEditMode) {
        // Update existing record
        console.log('Edit Mode - FormData:', formData);
        console.log('Edit Mode - empId:', formData.empId);
        if (!formData.empId) {
          throw new Error('Employee ID (empId) is required for update');
        }
        await updateUserDetailsRecord(formData);
        console.log('Employee updated successfully');
        setSuccessMessage('User updated successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        // Use provided employee ID or generate one if not provided
      
        const recordDataWithId = {
          ...formData,
         
        };
        
      
        console.log('Selected Role:', formData.role);
        
        // Create new record with provided or generated ID and selected role
        await createUserDetailsRecord(recordDataWithId);
        console.log('Employee created successfully');
        setSuccessMessage('User added successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
      
      // Clear any previous errors
      setError('');
      
      // Refresh the data
      await fetchUserDetailsData();
      
       // Close modal and reset form
       setIsModalOpen(false);
       setFormData({
         empId: '',
         employeeId: '',
         firstName: '',
         lastName: '',
         userName: '',
         employeeName: '',
         role: '',
         email: '',
         phoneNumber: '',
         alternatePhoneNumber: '',
         dateOfJoining: getTodayDate(),
         password: '',
       });
       setIsEditMode(false);
       
       // Auto-hide success message after 3 seconds
       setTimeout(() => {
         setSuccessMessage('');
       }, 3000);
      
    } catch (error) {
      console.error('Error saving employee:', error);
      setError(error.message || 'Failed to save employee data');
      setSuccessMessage(''); // Clear success message on error
      setSnackbarOpen(false);
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setPhoneNumberError('');
    setAlternatePhoneNumberError('');
    setFormData({
      empId: '',
      employeeId: '',
      empName: '',
      firstName: '',
      lastName: '',
      userName: '',
      employeeName: '',
      role: '',
      email: '',
      phoneNumber: '',
      alternatePhoneNumber: '',
      dateOfJoining: getTodayDate(),
      password: '',
    });
    setError('');
    setSuccessMessage('');
    setSnackbarOpen(false);
  };

  const handleEdit = (record) => {
    setPhoneNumberError('');
    setAlternatePhoneNumberError('');
    console.log('Edit Record:', record);
    console.log('Edit Record ID:', record.id);
    
    // Pre-fill the form with existing data
    const editId = record.empId;
    if (!editId) {
      console.error('No ID found in record:', record);
      setError('Cannot edit: Record ID is missing');
      return;
    }
    
    setFormData({
     
      empId: record.empId,
      employeeId:  record.employeeId,
      firstName: record.firstName,
      lastName: record.lastName,
      userName: String(record.userName || '').slice(0, 10),
      employeeName: '',
      role: record.role,
      email: record.email,
      phoneNumber: record.phoneNumber,
      alternatePhoneNumber: record.alternatePhoneNumber,
      dateOfJoining: record.dateOfJoining || '',
      password: record.password || '',
     //   attendanceDate: record.attendanceDate,
     //   timeIn: record.timeIn || '',
     //   timeOut: record.timeOut || '',
     //   status: record.status,
     //   photoBase64: record.photoBase64,
     //   photoTimestamp: record.photoTimestamp
    });
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    setRecordToDelete(id);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!recordToDelete) {
      console.error('No record ID to delete');
      return;
    }
    
    console.log('Confirm Delete - Record ID:', recordToDelete);
    
    try {
      await deleteUserDetailsRecord(recordToDelete);
      console.log('Employee deleted successfully');
      setSuccessMessage('Employee deleted successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      // Refresh the data after successful deletion
      await fetchUserDetailsData();
      // Close confirmation dialog
      setShowDeleteConfirm(false);
      setRecordToDelete(null);
    } catch (error) {
      console.error('Error deleting employee:', error);
      setError(error.message || 'Failed to delete employee');
      setSuccessMessage('Failed to delete employee');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      setShowDeleteConfirm(false);
      setRecordToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setRecordToDelete(null);
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    
    doc.setFontSize(16);
    doc.text('Users List Report', 14, 15);
    
    if (dateDisplay) {
      doc.setFontSize(10);
      doc.text(`Date Range: ${dateDisplay}`, 14, 22);
    }
    
    const tableData = filteredData.map(item => [
      item.employeeId ?? item.empId ?? '',
      `${item.firstName} ${item.lastName}`.trim(),
      item.role || '-',
      item.email || '-',
      item.phoneNumber || '-',
      item.alternatePhoneNumber || '-',
      item.dateOfJoining || '-'
    ]);
    
    autoTable(doc, {
      startY: dateDisplay ? 28 : 22,
      head: [['Employee ID', 'Name', 'Role', 'Email', 'Phone', 'Alternate Phone', 'Date of Joining']],
      body: tableData,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [0, 150, 136], textColor: 255 },
      margin: { top: 20 }
    });
    
    doc.save(`Users_List_Report_${new Date().toISOString().slice(0,10)}.pdf`);
  };

  const handleExportExcel = () => {
    const headers = ['Employee ID', 'Name', 'Role', 'Email', 'Phone', 'Alternate Phone', 'Date of Joining'];
    const rows = filteredData.map(item => [
      item.employeeId ?? item.empId ?? '',
      `${item.firstName} ${item.lastName}`.trim(),
      item.role || '-',
      item.email || '-',
      item.phoneNumber || '-',
      item.alternatePhoneNumber || '-',
      item.dateOfJoining || '-'
    ]);
    
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Users List');
    
    const columnWidths = [
      { wch: 12 }, { wch: 15 },
      { wch: 12 }, { wch: 20 }, { wch: 13 }, { wch: 15 }, { wch: 18 }
    ];
    worksheet['!cols'] = columnWidths;
    
    XLSX.writeFile(workbook, `Users_List_Report_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const handleGenerateReport = () => {
    const headers = ['Employee ID', 'Name', 'Role', 'Email', 'Phone', 'Alternate Phone', 'Date of Joining'];
    const rows = filteredData.map(item => [
      item.employeeId ?? item.empId ?? '',
      `${item.firstName} ${item.lastName}`.trim(),
      item.role || '-',
      item.email || '-',
      item.phoneNumber || '-',
      item.alternatePhoneNumber || '-',
      item.dateOfJoining || '-'
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => (v == null ? '' : String(v).replace(/"/g,'""'))).map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Users_List_Report_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };


  return (
    <>
      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        style={{position: 'absolute', bottom: '10px', left: '10px'}}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity}
          sx={{ 
            width: '100%', 
            backgroundColor: snackbarSeverity === 'success' ? '#43a047' : '#dc2626', 
            color: '#fff', 
            fontWeight: '400',
            fontSize: '18px' 
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Message */}
      {error && (
        <div className="bg-red-200 border border-red-400 text-red-700 px-4 py-3 rounded" style={{position: 'absolute', bottom: '10px', left: '10px', zIndex: '1000', minWidth: '200px'}}>
          {error}
        </div>
      )}
       
      <div className="space-y-6">

      {/* Loading State */}
      

      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-poppins">Users List</h1>
         
        </div>
        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={fetchUserDetailsData}
            className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            title="Refresh"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v6h6M20 20v-6h-6M20 8a8 8 0 00-14.828-3M4 16a8 8 0 0014.828 3" />
            </svg>
          
          </button>
          {/* Column Visibility Toggle */}
          <div className="relative column-menu-container">
            <button 
              onClick={() => setShowColumnMenu(!showColumnMenu)}
              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              title="Show/Hide Columns"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              
            </button>
            {showColumnMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-96 overflow-y-auto">
                <div className="p-2">
                  <div className="px-3 py-2 text-xs font-semibold text-gray-700 uppercase border-b border-gray-200">
                    Toggle Columns
                  </div>
                  <div className="px-3 py-2 border-b border-gray-200">
                    <button
                      type="button"
                      onClick={handleResetColumns}
                      className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Reset
                    </button>
                  </div>
                  {columnDefinitions.map((col) => {
                    const isLastVisible = visibleColumns[col.key] && getVisibleColumnCount() === 1;
                    return (
                      <label
                        key={col.key}
                        className={`flex items-center px-3 py-2 hover:bg-gray-100 ${
                          isLastVisible ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                        }`}
                        title={isLastVisible ? 'At least one column must remain visible' : ''}
                      >
                        <input
                          type="checkbox"
                          checked={visibleColumns[col.key]}
                          onChange={() => toggleColumn(col.key)}
                          disabled={isLastVisible}
                          className="mr-2 h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded disabled:cursor-not-allowed"
                        />
                        <span className={`text-sm ${isLastVisible ? 'text-gray-500' : 'text-gray-700'}`}>{col.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          {isAdministrator && (
            <>
              <button 
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                title={showFilters ? "Hide Filters" : "Show Filters"}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
              </svg>
              <span>{showFilters ? "Hide Filters" : "Show Filters"}</span>
            </button>
            </>
          )}
          <button 
            onClick={() => {
              setIsEditMode(false);
              setPhoneNumberError('');
              setAlternatePhoneNumberError('');
              setFormData({
                empId: "",
                employeeId: '',
                firstName: '',
                lastName: '',
                userName: '',
                employeeName: '',
                role: '',
                email: '',
                phoneNumber: '',
                alternatePhoneNumber: '',
                dateOfJoining: getTodayDate(),
                password: '',
              });
              setIsModalOpen(true);
            }}
            className="btn-primary"
          >
            Add User
          </button>
        </div>
      </div>
      
      {isAdministrator && showFilters && (
        <div className="p-4 flex flex-wrap items-end gap-4 bg-white shadow rounded-lg" style={{marginTop: '10px'}}>
          <div className="flex flex-col">
            <label className="text-xs text-gray-600 mb-1">
              {datePreset == '' ? 'Choose Date' : 'Selected Date Range: '} 
              {datePreset && <span className="font-bold text-black text-xs ">({datePreset})</span>}
            </label>
            <div className="relative date-dropdown-container">
              <div className="relative">
                <input
                  type="text"
                  value={dateDisplay || 'Select Range'}
                  readOnly
                  onClick={() => setShowDateDropdown(!showDateDropdown)}
                  className="w-full px-3 py-2 pl-10 pr-8 border border-gray-300 rounded-md text-sm bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Select Date Range"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                {dateDisplay && (
                  <button
                    type="button"
                    onClick={handleClearDate}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center hover:bg-gray-100 rounded-r-md"
                    title="Clear date"
                  >
                    <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              {showDateDropdown && (
                <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg z-20">
                  <div className="py-1">
                    <button
                      onClick={() => handleDatePresetChange('today')}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'today' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                    >
                      Today
                    </button>
                    <button
                      onClick={() => handleDatePresetChange('yesterday')}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'yesterday' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                    >
                      Yesterday
                    </button>
                    <button
                      onClick={() => handleDatePresetChange('last7')}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'last7' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                    >
                      Last 7 Days
                    </button>
                    <button
                      onClick={() => handleDatePresetChange('last30')}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'last30' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                    >
                      Last 30 Days
                    </button>
                    <button
                      onClick={() => handleDatePresetChange('thisMonth')}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'thisMonth' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                    >
                      This Month
                    </button>
                    <button
                      onClick={() => handleDatePresetChange('lastMonth')}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'lastMonth' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                    >
                      Last Month
                    </button>
                    <button
                      onClick={() => handleDatePresetChange('custom')}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'custom' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                    >
                      Custom Range
                    </button>
                  </div>
                </div>
              )}
              {/* Custom Calendar Modal */}
              {showCustomCalendar && (
                <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-30 p-4">
                  <div className="flex">
                    {/* Left side - Preset options */}
                    <div className="w-48 pr-4 border-r border-gray-200">
                      <div className="space-y-1">
                        <button
                          onClick={() => handleDatePresetChange('today')}
                          className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'today' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                        >
                          Today
                        </button>
                        <button
                          onClick={() => handleDatePresetChange('yesterday')}
                          className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'yesterday' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                        >
                          Yesterday
                        </button>
                        <button
                          onClick={() => handleDatePresetChange('last7')}
                          className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'last7' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                        >
                          Last 7 Days
                        </button>
                        <button
                          onClick={() => handleDatePresetChange('last30')}
                          className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'last30' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                        >
                          Last 30 Days
                        </button>
                        <button
                          onClick={() => handleDatePresetChange('thisMonth')}
                          className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'thisMonth' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                        >
                          This Month
                        </button>
                        <button
                          onClick={() => handleDatePresetChange('lastMonth')}
                          className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'lastMonth' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                        >
                          Last Month
                        </button>
                        <button
                          onClick={() => handleDatePresetChange('custom')}
                          className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'custom' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                        >
                          Custom Range
                        </button>
                      </div>
                    </div>
                    
                    {/* Right side - Calendar */}
                    <div className="ml-4">
                      {/* Calendar Navigation */}
                      <div className="flex items-center justify-between mb-4">
                        <button
                          onClick={() => navigateMonth(-1)}
                          className="p-1 hover:bg-gray-100 rounded"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <h3 className="text-sm font-medium text-gray-900">
                          {currentMonth.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                        </h3>
                        <button
                          onClick={() => navigateMonth(1)}
                          className="p-1 hover:bg-gray-100 rounded"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                      
                      {/* Calendar Grid */}
                      <div className="w-64">
                        {/* Days of week header */}
                        <div className="grid grid-cols-7 gap-1 mb-2">
                          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                            <div key={day} className="text-xs text-gray-500 text-center py-1">
                              {day}
                            </div>
                          ))}
                        </div>
                        
                        {/* Calendar days */}
                        <div className="grid grid-cols-7 gap-1">
                          {(() => {
                            const daysInMonth = getDaysInMonth(currentMonth);
                            const firstDay = getFirstDayOfMonth(currentMonth);
                            const days = [];
                            
                            // Previous month days
                            const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 0);
                            for (let i = firstDay - 1; i >= 0; i--) {
                              const day = prevMonth.getDate() - i;
                              days.push(
                                <button
                                  key={`prev-${day}`}
                                  className="text-xs text-gray-400 py-1 hover:bg-gray-100 rounded"
                                  disabled
                                >
                                  {day}
                                </button>
                              );
                            }
                            
                            // Current month days
                            for (let day = 1; day <= daysInMonth; day++) {
                              const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
                              const isSelected = isSameDate(date, customStartDate) || isSameDate(date, customEndDate);
                              const isInRange = customStartDate && customEndDate && isDateInRange(date, customStartDate, customEndDate);
                              
                              days.push(
                                <button
                                  key={day}
                                  onClick={() => handleDateClick(date)}
                                  className={`text-xs py-1 rounded hover:bg-gray-100 ${
                                    isSelected 
                                      ? 'bg-orange-500 text-white' 
                                      : isInRange 
                                      ? 'bg-orange-100 text-orange-600' 
                                      : 'text-gray-700'
                                  }`}
                                >
                                  {day}
                                </button>
                              );
                            }
                            
                            // Next month days
                            const remainingCells = 42 - days.length; // 6 rows * 7 days
                            for (let day = 1; day <= remainingCells; day++) {
                              days.push(
                                <button
                                  key={`next-${day}`}
                                  className="text-xs text-gray-400 py-1 hover:bg-gray-100 rounded"
                                  disabled
                                >
                                  {day}
                                </button>
                              );
                            }
                            
                            return days;
                          })()}
                        </div>
                      </div>
                      
                      {/* Selected date range display */}
                      <div className="mt-4 text-center">
                        <div className="text-sm text-gray-600 mb-3">
                          {customStartDate && customEndDate 
                            ? `${formatDate(customStartDate)} - ${formatDate(customEndDate)}`
                            : customStartDate 
                            ? `${formatDate(customStartDate)} - Select end date`
                            : 'Select start date'
                          }
                        </div>
                        
                        {/* Action buttons */}
                        <div className="flex gap-2 justify-center">
                          <button
                            onClick={handleCancelCustomRange}
                            className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleApplyCustomRange}
                            disabled={!customStartDate || !customEndDate}
                            className="px-4 py-2 text-sm bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col max-w-[180px]">
            <label className="text-xs text-gray-600 mb-1">Employee ID</label>
            <div className="relative employee-id-autocomplete-container">
              <input
                type="text"
                value={employeeIdInputValue}
                onChange={handleEmployeeIdInputChange}
                placeholder="Search "
                className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                autoComplete="off"
              />
              <div className="absolute inset-y-0 right-0 flex items-center">
                {employeeIdInputValue ? (
                  <button
                    type="button"
                    onClick={handleClearEmployeeId}
                    className="pr-3 hover:bg-gray-100 rounded-r-md flex items-center"
                    title="Clear employee ID"
                  >
                    <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                ) : (
                  <div className="pr-3 pointer-events-none">
                    <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col max-w-[180px]">
            <label className="text-xs text-gray-600 mb-1">Name</label>
            <div className="relative name-autocomplete-container">
              <input
                type="text"
                value={nameInputValue}
                onChange={handleNameInputChange}
                placeholder="Search "
                className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                autoComplete="off"
              />
              <div className="absolute inset-y-0 right-0 flex items-center">
                {nameInputValue ? (
                  <button
                    type="button"
                    onClick={handleClearName}
                    className="pr-3 hover:bg-gray-100 rounded-r-md flex items-center"
                    title="Clear name"
                  >
                    <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                ) : (
                  <div className="pr-3 pointer-events-none">
                    <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col max-w-[180px]">
            <label className="text-xs text-gray-600 mb-1">Role</label>
            <div className="relative role-autocomplete-container">
              <input
                type="text"
                value={roleInputValue}
                onChange={handleRoleInputChange}
                onFocus={() => {
                  setShowRoleDropdown(true);
                }}
                placeholder="Select "
                className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                autoComplete="off"
              />
              <div className="absolute inset-y-0 right-0 flex items-center">
                {roleInputValue ? (
                  <button
                    type="button"
                    onClick={handleClearRole}
                    className="pr-3 hover:bg-gray-100 rounded-r-md flex items-center"
                    title="Clear role"
                  >
                    <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                ) : (
                  <div className="pr-3 pointer-events-none">
                    <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                )}
              </div>
              {showRoleDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                  <div className="py-1">
                    {filteredRoleOptions.length > 0 ? (
                      filteredRoleOptions.map(opt => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => handleRoleSelect(opt)}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 focus:bg-orange-100 focus:outline-none ${
                            selectedRole === opt.value ? 'bg-orange-100 text-orange-600 font-medium' : 'text-gray-700'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-sm text-gray-500 text-center">
                        No matches found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
         
          <div className="ml-auto flex items-center gap-2">
            <button 
              onClick={handleClearFilters}
              disabled={!hasActiveFilters}
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
              title="Clear Filters"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span>Clear All</span>
            </button>
            <button 
              onClick={handleExportPDF}
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              title="Export as PDF"
            >
              <img src="/assets/img/pdf.png" alt="PDF" className="h-4 w-4" />
            </button>
            <button 
              onClick={handleExportExcel}
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              title="Export as Excel"
            >
              <img src="/assets/img/sheet.png" alt="Excel" className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="bg-white shadow rounded-lg p-8 flex items-center justify-center min-h-[400px]">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
            <p className="text-gray-600 text-sm">Loading data...</p>
          </div>
        </div>
      ) : (
        <Grid cols={1} className="p-0 flex flex-wrap items-end rounded-lg" style={{marginTop: '10px'}}>
          <div className="">
            <div className="overflow-x-auto">
              <div 
                className={`relative overflow-y-auto ${!showFilters ? 'bg-white' : ''}`}
                style={isAdministrator ? { height: 'calc(100vh - 370px)', backgroundColor: '#fff' } :  { height: 'calc(100vh - 270px)',backgroundColor: '#fff' }
                }
              >
                <table className="min-w-full divide-y divide-gray-200 shadow rounded-lg border border-gray-200">
                  <thead className="sticky top-0 z-10" style={{backgroundColor: "rgb(0, 150, 136)"}}>
                    <tr>
                      {visibleColumns.employeeId && (
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('employeeId')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Emp ID</span>
                            {getSortIcon('employeeId')}
                          </div>
                        </th>
                      )}
                      {visibleColumns.dateOfJoining && (
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('dateOfJoining')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Date of Joining</span>
                            {getSortIcon('dateOfJoining')}
                          </div>
                        </th>
                      )}
                      {visibleColumns.name && (
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('name')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Name</span>
                            {getSortIcon('name')}
                          </div>
                        </th>
                      )}
                      {visibleColumns.userName && (
                        <th
                          className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('userName')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Username</span>
                            {getSortIcon('userName')}
                          </div>
                        </th>
                      )}
                      {visibleColumns.role && (
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('role')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Role</span>
                            {getSortIcon('role')}
                          </div>
                        </th>
                      )}
                      {visibleColumns.email && (
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('email')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Email</span>
                            {getSortIcon('email')}
                          </div>
                        </th>
                      )}
                      {visibleColumns.phone && (
                        <th 
                          className="px-6 py-3 text-right text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('phoneNumber')}
                        >
                          <div className="flex items-center justify-end space-x-1">
                            <span>Phone</span>
                            {getSortIcon('phoneNumber')}
                          </div>
                        </th>
                      )}
                      {visibleColumns.alternatePhone && (
                        <th 
                          className="px-6 py-3 text-right text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('alternatePhoneNumber')}
                        >
                          <div className="flex items-center justify-end space-x-1">
                            <span>Alternate Phone</span>
                            {getSortIcon('alternatePhoneNumber')}
                          </div>
                        </th>
                      )}
                      {/* {visibleColumns.password && (
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('password')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Password</span>
                            {getSortIcon('password')}
                          </div>
                        </th>
                      )}
                       */}
                      {visibleColumns.actions && (
                        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {!isLoading && paginatedData.length === 0 ? (
                      <tr>
                        <td colSpan={getVisibleColumnCount()} className="px-6 py-8 text-center text-sm text-gray-500">
                          No user records found.
                        </td>
                      </tr>
                    ) : (
                      paginatedData.map((record) => (
                        <tr key={record.employeeId || record.id}>
                          {visibleColumns.employeeId && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{record.employeeId}</td>
                          )}

                           {visibleColumns.dateOfJoining && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm">{formatDateMMMDDYYYY(record.dateOfJoining)}</td>
                          )}
                          {visibleColumns.name && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              <button
                                // onClick={() => handleEmployeeClick(record)}
                                className=" cursor-pointer"
                              >
                                {`${record.firstName} ${record.lastName}`.trim()}
                              </button>
                            </td>
                          )}
                          {visibleColumns.userName && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              {record.userName || '-'}
                            </td>
                          )}
                          {visibleColumns.role && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600 font-semibold">{record.role || 'N/A'}</td>
                          )}
                          {visibleColumns.email && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm">{record.email || '-'}</td>
                          )}
                          {visibleColumns.phone && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right">{record.phoneNumber || '-'}</td>
                          )}
                          {visibleColumns.alternatePhone && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right">{record.alternatePhoneNumber || '-'}</td>
                          )}
                          {/* {visibleColumns.password && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">
                              {record.password ? '••••••••' : '-'}
                            </td>
                          )} */}
                         
                          {visibleColumns.actions && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleEdit(record)}
                                  className="text-indigo-600 hover:text-indigo-900 focus:outline-none focus:underline"
                                  title="Edit"
                                >
                                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleDelete(record.empId)}
                                  className="text-red-600 hover:text-red-900 focus:outline-none focus:underline"
                                  title="Delete"
                                >
                                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        itemsPerPage={itemsPerPage}
        totalItems={filteredData.length}
        onItemsPerPageChange={handleItemsPerPageChange}
      />
          </div>
        </div>
      </Grid>
      )}

      {/* Add Employee Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50" style={{marginTop: 0}}>
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={handleClose}
            ></div>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all modal-card">
              <form onSubmit={handleSubmit}>
                {/* Header */}
                 <div className="bg-white px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                   <h3 className="text-lg font-medium text-gray-900">
                     {isEditMode ? 'Edit User' : 'Add User'}
                   </h3>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                 {/* Form content */}
                 <div className="bg-white px-6 py-4">
                   <div className="grid grid-cols-3 gap-6 mb-4 " >
                     {/* Employee ID - Only show in Edit mode */}
                     {isEditMode && (
                       <div className='mb-8'>
                         <label className="block text-sm font-medium text-gray-700 mb-1">
                           Employee ID
                         </label>
                         <input
                           type="text"
                           name="employeeId"
                           value={formData.employeeId}
                           disabled
                           className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed readonly"
                         />
                       </div>
                     )}

                      {/* Date of Joining */}
                      <div className='mb-8'>
                       <label className="block text-sm font-medium text-gray-700 mb-1">
                         Date of Joining
                       </label>
                       <input
                         type="date"
                         name="dateOfJoining"
                         value={formData.dateOfJoining}
                         onChange={handleInputChange}
                         max={getTodayDate()}
                         className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                       />
                     </div>
                     
                     {/* First Name */}
                     <div className='mb-8'>
                       <label className="block text-sm font-medium text-gray-700  mb-1">
                         First Name <span className="text-red-500">*</span>
                       </label>
                       <input
                         type="text"
                         name="firstName"
                         value={formData.firstName}
                         onChange={handleInputChange}
                         required
                         className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                       />
                     </div>

                     {/* Last Name */}
                     <div className='mb-8'>
                       <label className="block text-sm font-medium text-gray-700  mb-1">
                         Last Name 
                       </label>
                       <input
                         type="text"
                         name="lastName"
                         value={formData.lastName}
                         onChange={handleInputChange}
                         
                         className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                       />
                     </div>

                     {/* Username */}
                     <div className='mb-8'>
                       <label className="block text-sm font-medium text-gray-700  mb-1">
                         Username <span className="text-red-500">*</span>
                         <span className="text-gray-500 font-normal text-xs ml-1">(max 10 characters)</span>
                       </label>
                       <input
                         type="text"
                         name="userName"
                         value={formData.userName || ''}
                         onChange={handleInputChange}
                         maxLength={10}
                         required
                         autoComplete="username"
                         className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                       />
                     </div>

                     {/* Role - Dropdown */}
                     <div className='mb-8'>
                       <label className="block text-sm font-medium text-gray-700  mb-1">
                         Role <span className="text-red-500">*</span>
                       </label>
                       <select
                         name="role"
                         value={formData.role}
                         onChange={handleInputChange}
                         required
                         className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                       >
                        <option value="">Select</option>
                        {roleOptionsFromApi.length > 0 ? (
                          roleOptionsFromApi.map((role) => (
                            <option key={role.value} value={role.value}>
                              {role.label}
                            </option>
                          ))
                        ) : (
                          <>
                            <option value="Manager">Manager</option>
                            <option value="Staff">Staff</option>
                            <option value="Supervisor">Supervisor</option>
                          </>
                        )}
                       </select>
                     </div>

                     {/* Attendance Date */}
                     <div className='mb-8'>
                       <label className="block text-sm font-medium text-gray-700  mb-1">
                         Email 
                       </label>
                       <input
                         type="email"
                         name="email"
                         value={formData.email}
                         onChange={handleInputChange}
                         className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                       />
                     </div> 

                     {/* Phone Number */}
                     <div className='mb-8'>
                       <label className="block text-sm font-medium text-gray-700 mb-1">
                         Phone Number <span className="text-red-500">*</span>
                       </label>
                       <input
                         type="tel"
                         name="phoneNumber"
                         value={formData.phoneNumber}
                         onChange={handleInputChange}
                         inputMode="numeric"
                         pattern="[0-9]*"
                         className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${phoneNumberError ? 'border-red-500' : 'border-gray-300'}`}
                         placeholder=""
                       />
                       {phoneNumberError && (
                         <p className="mt-1 text-sm text-red-600">{phoneNumberError}</p>
                       )}
                     </div>

                     {/* Alternate Phone */}
                     <div className='mb-8'>
                       <label className="block text-sm font-medium text-gray-700 mb-1">
                         Alternate Phone
                       </label>
                       <input
                         type="tel"
                         name="alternatePhoneNumber"
                         value={formData.alternatePhoneNumber}
                         onChange={handleInputChange}
                         inputMode="numeric"
                         pattern="[0-9]*"
                         className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${alternatePhoneNumberError ? 'border-red-500' : 'border-gray-300'}`}
                         placeholder=""
                       />
                       {alternatePhoneNumberError && (
                         <p className="mt-1 text-sm text-red-600">{alternatePhoneNumberError}</p>
                       )}
                     </div>

                     {/* Password */}
                     <div className='mb-8'>
                       <label className="block text-sm font-medium text-gray-700 mb-1">
                        Password <span className="text-red-500">*</span>
                       </label>
                      <div className="relative">
                        <input
                          required
                          type={showPassword ? 'text' : 'password'}
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((prev) => !prev)}
                          className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          title={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? (
                            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-5 0-9-4-10-7a18.45 18.45 0 0 1 5.06-5.94" />
                              <path d="M1 1l22 22" />
                              <path d="M9.9 4.24A10.93 10.93 0 0 1 12 5c5 0 9 4 10 7a18.2 18.2 0 0 1-2.16 3.19" />
                              <path d="M14.12 14.12A3 3 0 0 1 9.88 9.88" />
                            </svg>
                          ) : (
                            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          )}
                        </button>
                      </div>
                     </div>

                   
                   </div>

                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-6 py-3 flex justify-end space-x-3 modal-card-bottom">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    Close
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    style={{ backgroundColor: '#009688' }}
                  >
                   {isEditMode ? 'Update' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Background overlay */}
          <div 
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            onClick={handleCancelDelete}
          ></div>

          {/* Confirmation Dialog */}
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 transform transition-all">
            <div className="p-6">
              {/* Warning Icon */}
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
                <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              
              {/* Message */}
              <h3 className="text-lg font-medium text-gray-900 text-center mb-2">
                Confirm Delete
              </h3>
              <p className="text-sm text-gray-500 text-center mb-6">
                Are you sure you want to delete this employee? This action cannot be undone.
              </p>
              
              {/* Action buttons */}
              <div className="flex gap-3 justify-center">
                <button
                  type="button"
                  onClick={handleCancelDelete}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  className="px-6 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 hover:opacity-90"
                  style={{ backgroundColor: '#dc2626' }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default UserDetails;

